import React from 'react';
import openAiIcon from '../../assets/ai-providers/openai.svg';
import {
  Box,
  Chip,
  SvgIcon,
  Tooltip,
  Typography
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import { normalizeEmails } from '../../helper/spreadsheetView';
import { TableSortIndicator } from '../../helper/tableSortIndicator';
import { ESTIMATED_LABEL, ESTIMATED_TOOLTIP, NOT_REPORTED, metricSortValue } from '../../helper/metricBuckets';

/** Active / inactive fills from the SMB · MBU · ENT size glyph set. */
const SIZE_BAR_ACTIVE = '#4A2CF5';
const SIZE_BAR_INACTIVE = '#D1D5DB';
const SIZE_BAR_INACTIVE_DARK = 'rgba(255, 255, 255, 0.28)';

/**
 * Company-size glyph (SMB / MBU / ENT): always three ascending rounded bars.
 * Active bars use AlphaSearch purple; remaining bars stay muted gray.
 * bars=1 → Small · bars=2 → Medium · bars=3 → Enterprise
 */
export function CompanySizeBarsIcon({ bars = 1, inactiveColor, ...props }) {
  const activeCount = Math.max(1, Math.min(3, Number(bars) || 1));
  const muted = inactiveColor || SIZE_BAR_INACTIVE;

  // 16×16 viewBox — three pill bars, ascending L→R, shared baseline.
  const barWidth = 3.2;
  const gap = 1.6;
  const radii = 1.6;
  const heights = [8, 11.5, 15];
  const totalWidth = 3 * barWidth + 2 * gap;
  const startX = (16 - totalWidth) / 2;
  const baseline = 15.5;

  return (
    <SvgIcon
      viewBox="0 0 16 16"
      data-testid="company-size-bars-icon"
      data-bars={activeCount}
      inheritViewBox
      {...props}
    >
      {heights.map((height, index) => {
        const x = startX + index * (barWidth + gap);
        const y = baseline - height;
        const isActive = index < activeCount;
        return (
          <rect
            key={index}
            x={x}
            y={y}
            width={barWidth}
            height={height}
            rx={radii}
            ry={radii}
            fill={isActive ? SIZE_BAR_ACTIVE : muted}
            data-active={isActive ? 'true' : 'false'}
          />
        );
      })}
    </SvgIcon>
  );
}

export const ALL_COLUMNS = [
  { key: 'name', name: 'Name', frozen: true },
  { key: 'description', name: 'Description' },
  { key: 'industry', name: 'Industry' },
  { key: 'size', name: 'Size' },
  { key: 'location', name: 'Location' },
  { key: 'domain', name: 'Domain' },
  { key: 'emails', name: 'Emails' },
  { key: 'phone', name: 'Phone' },
  { key: 'revenue', name: 'Revenue' }
];

export const DEFAULT_VISIBLE = new Set(['name', 'description', 'industry', 'size', 'location', 'domain']);

export const LEAD_CONTACT_COLUMNS = [
  { actionKey: 'lead_contact_emails', name: 'Lead Emails' },
  { actionKey: 'lead_contact_phones', name: 'Lead Phone Numbers' },
  { actionKey: 'lead_contact_linkedin', name: 'Lead LinkedIn Profiles' }
];

const LEAD_CONTACT_NAMES_BY_ACTION = new Map(
  LEAD_CONTACT_COLUMNS.map((column) => [column.actionKey, column.name])
);

const getCanonicalLeadContactColumn = (name) => {
  if (typeof name !== 'string') return null;
  return LEAD_CONTACT_COLUMNS.find((column) => name.startsWith(column.name)) || null;
};

export const isLeadContactColumnName = (name) => Boolean(getCanonicalLeadContactColumn(name));

export const isLeadContactActionKey = (actionKey) => LEAD_CONTACT_NAMES_BY_ACTION.has(actionKey);

export const getLeadContactColumnLabel = (actionKey) => LEAD_CONTACT_NAMES_BY_ACTION.get(actionKey) || null;

export const getEnrichmentActionLabel = (actionKey) => {
  const leadContactLabel = getLeadContactColumnLabel(actionKey);
  if (leadContactLabel) return leadContactLabel;
  if (actionKey === 'find_email') return 'Find Email';
  if (actionKey === 'find_and_verify_email') return 'Find Verified Emails';
  if (actionKey === 'verify_email') return 'Verify Email';
  if (actionKey?.startsWith('ai_enrichment')) {
    const runId = actionKey.split('_')[2];
    return `AI Enrichment ${runId || ''}`.trim();
  }
  return actionKey || 'Enrichment';
};

export const renderSpreadsheetHeaderCell = ({ column, sortDirection }) => (
  <Box
    component="span"
    data-testid="spreadsheet-header-cell"
    data-sorted={sortDirection ? 'true' : 'false'}
    sx={{
      alignItems: 'center',
      display: 'flex',
      inset: 0,
      minWidth: 0,
      px: 1,
      position: 'absolute'
    }}
  >
    {/* Inline group keeps the sort icon beside the label (not flush-right). */}
    <Box
      component="span"
      sx={{
        alignItems: 'center',
        display: 'inline-flex',
        gap: 0.5,
        maxWidth: '100%',
        minWidth: 0,
      }}
    >
      <Typography
        component="span"
        title={String(column.name || '')}
        sx={{
          color: 'inherit',
          flex: '0 1 auto',
          fontSize: '12px',
          fontWeight: 800,
          lineHeight: 1.2,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        {column.name}
      </Typography>
      {column.sortable ? (
        <TableSortIndicator sortDirection={sortDirection} />
      ) : null}
    </Box>
  </Box>
);

const normalizeContactValues = (values) => {
  if (!Array.isArray(values)) return [];

  return values.reduce((acc, value) => {
    const text = String(value || '').trim();
    if (text && !acc.includes(text)) acc.push(text);
    return acc;
  }, []);
};

const splitContactCellValue = (value) => {
  if (Array.isArray(value)) return normalizeContactValues(value);
  return String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

export const normalizeLeadDerivedColumns = (derivedColumns) => {
  if (!derivedColumns || typeof derivedColumns !== 'object' || Array.isArray(derivedColumns)) return [];

  return Object.entries(derivedColumns)
    .map(([name, rawValues]) => {
      const canonicalColumn = getCanonicalLeadContactColumn(name);
      const values = normalizeContactValues(rawValues);
      if (!canonicalColumn || values.length === 0) return null;
      if (name === canonicalColumn.name) return { ...canonicalColumn, values };
      return { actionKey: name, name, values };
    })
    .filter(Boolean);
};

export const isLeadContactEnrichmentPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return false;
  const queryCategory = String(payload.query_category || '').trim().toLowerCase();
  return queryCategory === 'lead_contact' || normalizeLeadDerivedColumns(payload.derived_columns).length > 0;
};

export const mergeLeadContactValues = (currentValue, nextValues) => {
  const merged = splitContactCellValue(currentValue);
  normalizeContactValues(nextValues).forEach((value) => {
    if (!merged.includes(value)) merged.push(value);
  });
  return merged.join(', ');
};

export const insertLeadContactColumns = (currentColumns, derivedEntries, afterActionKey) => {
  const isLeadContactKey = (actionKey) => (
    isLeadContactActionKey(actionKey) || isLeadContactColumnName(actionKey)
  );
  const derivedActionKeys = derivedEntries.map((entry) => entry.actionKey);
  const missing = derivedActionKeys.filter((actionKey) => !currentColumns.includes(actionKey));
  if (missing.length === 0) return currentColumns;

  const presentLeadContactKeys = [
    ...currentColumns.filter(isLeadContactKey),
    ...missing
  ].filter((actionKey, index, keys) => keys.indexOf(actionKey) === index);
  const orderedLeadContactKeys = LEAD_CONTACT_COLUMNS.flatMap((column) => (
    presentLeadContactKeys.filter((actionKey) => (
      actionKey === column.actionKey || String(actionKey).startsWith(column.name)
    ))
  ));
  const next = currentColumns.filter((actionKey) => !isLeadContactKey(actionKey));
  const insertIndex = next.indexOf(afterActionKey);
  if (insertIndex === -1) next.push(...orderedLeadContactKeys);
  else next.splice(insertIndex + 1, 0, ...orderedLeadContactKeys);
  return next;
};

export const mergeLeadDerivedStatusMap = (currentStatusMap, rowId, derivedEntries) => {
  const next = { ...(currentStatusMap || {}) };
  derivedEntries.forEach((entry) => {
    const actionObj = { ...(next[entry.actionKey] || {}) };
    const existing = actionObj[rowId]?.status === 'success' ? actionObj[rowId].result : '';
    actionObj[rowId] = {
      status: 'success',
      result: mergeLeadContactValues(existing, entry.values)
    };
    next[entry.actionKey] = actionObj;
  });
  return next;
};

const AnthropicIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <text x="3" y="19" fontSize="21" fontWeight="900" fontFamily="sans-serif" fill="currentColor">A\</text>
  </SvgIcon>
);

export const getProviderIcon = (provider) => {
  if (provider === 'openai') return <Box component="span" aria-hidden="true" sx={{ width: 20, height: 20, display: "inline-block", bgcolor: "#10a37f", mask: `url(${openAiIcon}) center / contain no-repeat`, WebkitMask: `url(${openAiIcon}) center / contain no-repeat` }} />;
  if (provider === 'anthropic') return <AnthropicIcon fontSize="small" sx={{ color: '#000000' }} />;
  return <AutoAwesomeIcon fontSize="small" sx={{ color: '#4285F4' }} />;
};

const HEADCOUNT_BUCKETS = [
  { max: 10, label: '2-10', color: '#2196f3' },
  { max: 50, label: '11-50', color: '#4caf50' },
  { max: 200, label: '51-200', color: '#f44336' },
  { max: 500, label: '201-500', color: '#9c27b0' },
  { max: 1000, label: '501-1000', color: '#ffffff' },
  { max: 5000, label: '1001-5000', color: '#ff9800' },
  { max: 10000, label: '5001-10000', color: '#ffeb3b' },
  { max: Infinity, label: '10000+', color: '#795548' },
];

/** Visual company-size tiers mapped from HEADCOUNT_BUCKETS (SMB / MBU / ENT glyphs). */
export const COMPANY_SIZE_CATEGORIES = {
  small: {
    id: 'small',
    label: 'Small company',
    bars: 1,
  },
  medium: {
    id: 'medium',
    label: 'Medium company',
    bars: 2,
  },
  enterprise: {
    id: 'enterprise',
    label: 'Enterprise company',
    bars: 3,
  },
};

/**
 * Map a headcount bucket max to Small / Medium / Enterprise.
 * Small: ≤50 · Medium: ≤500 · Enterprise: >500
 * @param {number} bucketMax
 * @returns {'small' | 'medium' | 'enterprise'}
 */
export const getCompanySizeCategoryId = (bucketMax) => {
  if (bucketMax <= 50) return 'small';
  if (bucketMax <= 500) return 'medium';
  return 'enterprise';
};

const HEADCOUNT_CHIP_ICON_SX = {
  width: 16,
  height: 16,
  fontSize: '16px !important',
};

/**
 * Size column chip: a neutral outlined tag with a company-size bars icon and a
 * category tooltip.
 * @param {number|string|null|undefined} value sort value (metricSortValue: lower bound or point)
 * @param {string|null} [bucketLabel] formatted bucket text (helper/metricBuckets, FE #650)
 * @param {boolean} [estimated] description-derived value (FE #749)
 */
export const renderHeadcountBucket = (value, bucketLabel, estimated = false) => {
  if (!bucketLabel && (!value || value < 1)) return <Typography variant="caption">{NOT_REPORTED}</Typography>;

  const bucket = HEADCOUNT_BUCKETS.find((b) => (value || 0) <= b.max);
  const label = `${bucketLabel || bucket.label} employees`;

  const categoryId = getCompanySizeCategoryId(bucket.max);
    const category = COMPANY_SIZE_CATEGORIES[categoryId];

    return (
      <Tooltip title={estimated ? `${category.label} · ${ESTIMATED_TOOLTIP}` : category.label} placement="top" arrow>
        <Chip
          label={estimated ? (
            <>
              <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {label}
              </Box>{' '}
              <Box component="span" sx={{ flexShrink: 0, ml: '4px' }}>
                ({ESTIMATED_LABEL})
              </Box>
            </>
          ) : label}
          size="small"
          variant="outlined"
          data-testid="headcount-size-chip"
          data-size-category={category.id}
          icon={
            <CompanySizeBarsIcon
              bars={category.bars}
              sx={HEADCOUNT_CHIP_ICON_SX}
              aria-hidden="true"
            />
          }
          sx={(theme) => {
            const isDark = theme.palette.mode === 'dark';
            return {
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.04)' : '#F7F7F8',
              color: theme.palette.text.primary,
              borderColor: isDark
                ? 'rgba(255, 255, 255, 0.18)'
                : 'rgba(0, 0, 0, 0.12)',
              fontWeight: 600,
              fontSize: '11px',
              height: '20px',
              borderRadius: '6px',
              '& .MuiChip-icon': {
                ml: '4px',
                mr: '-2px',
                width: 16,
                height: 16,
                // Explicit SVG fills (purple / gray) — don't tint via currentColor.
                color: 'unset',
              },
              // Dark-mode inactive bars need a lighter muted fill.
              '& .MuiChip-icon rect[data-active="false"]': {
                fill: isDark ? SIZE_BAR_INACTIVE_DARK : SIZE_BAR_INACTIVE,
              },
              '& .MuiChip-label': {
                px: '6px',
                ...(estimated ? { display: 'flex', alignItems: 'center', minWidth: 0 } : {}),
              },
            };
          }}
        />
      </Tooltip>
    );
};

export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      return true;
    } catch {
      return false;
    }
  }
};

const normalizeSortText = (value) => {
  if (Array.isArray(value)) return normalizeSortText(value[0]);
  if (value === null || value === undefined) return '';
  return String(value).trim().toLowerCase();
};

const normalizeSortNumber = (value) => {
  if (value === null || value === undefined || value === '' || value === 'N/A') return null;
  if (typeof value === 'object') return metricSortValue(value);

  const cleaned = typeof value === 'string'
    ? value.replace(/[$,\s]/g, '')
    : value;
  const number = Number(cleaned);
  return Number.isFinite(number) ? number : null;
};

const isBlankSortValue = (value) => value === null || value === undefined || value === '';

const compareSortValues = (left, right, direction = 'ASC') => {
  const leftBlank = isBlankSortValue(left);
  const rightBlank = isBlankSortValue(right);
  if (leftBlank && rightBlank) return 0;
  if (leftBlank) return 1;
  if (rightBlank) return -1;

  const directionFactor = direction === 'DESC' ? -1 : 1;
  if (typeof left === 'number' && typeof right === 'number') {
    return (left - right) * directionFactor;
  }

  return String(left).localeCompare(String(right), undefined, {
    numeric: true,
    sensitivity: 'base'
  }) * directionFactor;
};

const getEnrichmentSortValue = (actionKey, entry) => {
  if (!entry) return '';
  if (actionKey === 'verify_email' && entry.status === 'success') {
    return normalizeSortNumber(entry.result);
  }
  if (entry.status === 'success') return normalizeSortText(entry.result);
  if (entry.status === 'running') return 'running';
  return normalizeSortText(entry.error || entry.status);
};

const getGridSortValue = (row, columnKey, statusMap) => {
  if (columnKey.startsWith('enrich_')) {
    const actionKey = columnKey.replace(/^enrich_/, '');
    return getEnrichmentSortValue(actionKey, statusMap?.[actionKey]?.[row.id]);
  }
  if (columnKey === 'revenue' || columnKey === 'size') return normalizeSortNumber(row[columnKey]);
  if (columnKey === 'emails') return normalizeSortText(normalizeEmails(row.emails)[0]);
  if (columnKey === 'location') {
    const primary =
      (Array.isArray(row.locations) && row.locations[0]) || row.location || '';
    return normalizeSortText(primary);
  }
  return normalizeSortText(row[columnKey]);
};

export const sortGridRows = (rows, sortColumns, statusMap) => {
  const activeSort = sortColumns?.[0];
  if (!activeSort) return rows;

  return rows
    .map((row, index) => ({ row, index }))
    .sort((left, right) => {
      const result = compareSortValues(
        getGridSortValue(left.row, activeSort.columnKey, statusMap),
        getGridSortValue(right.row, activeSort.columnKey, statusMap),
        activeSort.direction
      );
      return result || left.index - right.index;
    })
    .map(({ row }) => row);
};
